import type { Contract } from '@/types/api/contract'

import { isNativePlatform } from '@/lib/capacitor'

const STORAGE_KEY = 'amo-last-contract-id'

// Dernier contrat choisi par l'utilisateur (persisté entre les pages et les sessions).
// Si l'id ne correspond à aucun contrat du compte connecté (autre utilisateur,
// contrat résilié…), les consommateurs retombent sur le premier contrat de la liste.
// Sur l'app native, le localStorage de la WebView peut être purgé par l'OS : la valeur
// est doublée dans le stockage natif (@capacitor/preferences) et restaurée au démarrage.
export const getLastContractId = (): string | null => {
    try {
        return window.localStorage.getItem(STORAGE_KEY)
    } catch {
        return null
    }
}

export const setLastContractId = (id: string | number): void => {
    try {
        window.localStorage.setItem(STORAGE_KEY, String(id))
    } catch {
        /* stockage indisponible (navigation privée…) : sélection non persistée */
    }
    if (isNativePlatform()) {
        import('@capacitor/preferences')
            .then(({ Preferences }) => Preferences.set({ key: STORAGE_KEY, value: String(id) }))
            .catch(() => {})
    }
}

// À appeler au démarrage de l'app native : re-seed le localStorage depuis le stockage natif
export const restoreLastContractId = async (): Promise<void> => {
    try {
        const { Preferences } = await import('@capacitor/preferences')
        const { value } = await Preferences.get({ key: STORAGE_KEY })
        if (value) {
            window.localStorage.setItem(STORAGE_KEY, value)
        }
    } catch {
        /* plugin indisponible : on reste sur le localStorage seul */
    }
}

// Retourne le dernier contrat choisi s'il est présent dans la liste, sinon le premier.
export const getDefaultContract = (contracts?: Contract[] | null): Contract | undefined => {
    const lastId = getLastContractId()
    return contracts?.find((c) => String(c.id) === lastId) || contracts?.[0]
}
